import { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import classes from './InputFileCSV.module.css';
import clsx from 'clsx';
import File from './CSVFile';
import { Trans, useTranslation } from 'react-i18next';

const VALID_FILE_TYPES = ['text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];

export default function InputFileCSV(props) {
    const { file, onChange, extraFileExtensions = [], dropText = 'drop-csv-xlsx-file' } = props;

    const accept = [...VALID_FILE_TYPES, ...extraFileExtensions].join(', ');

    const validateFile = (file) => {
        if (VALID_FILE_TYPES.includes(file?.type)) {
            return true;
        }

        // Browsers report no MIME type for uncommon extensions, so they are matched by name
        return extraFileExtensions.some((extension) => file?.name?.toLowerCase().endsWith(extension));
    };

    const [dragging, setDragging] = useState(false);
    const timer = useRef(null);

    const { t } = useTranslation('common');

    const debounceDragLeave = useCallback(() => {
        timer.current = setTimeout(() => {
            setDragging(false);
        }, 100);
    }, []);

    const dragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();

        clearTimeout(timer.current);
        setDragging(true);
    }, []);

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();

        setDragging(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const file = e.dataTransfer.files[0];
            const isValid = validateFile(file);
            if (isValid) {
                onChange(file);
            }
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        const isValid = validateFile(file);
        if (isValid) {
            onChange(file);
        }
    };

    if (file) {
        return (
            <div className="py-3">
                <File file={file} onDelete={() => onChange(null)} />
            </div>
        );
    }

    return (
        <div
            className={clsx(classes.dropzone, dragging && classes.dragging)}
            onDragEnter={dragEnter}
            onDragLeave={debounceDragLeave}
            onDragOver={dragEnter}
            onDrop={handleDrop}
        >
            <label className={classes.inputFileContainer}>
                <div className={classes.inputFileButton}>
                    <div>
                        <Trans t={t} i18nKey={dropText}>
                            Soltar un archivo CSV aquí o
                            <span className={classes.link}>haga clic para seleccionar uno</span>
                        </Trans>
                    </div>
                </div>
                <input
                    type="file"
                    name="files[]"
                    className={classes.inputFile}
                    accept={accept}
                    onChange={handleFileChange}
                />
            </label>
        </div>
    );
}

InputFileCSV.propTypes = {
    file: PropTypes.shape({
        name: PropTypes.string.isRequired,
        size: PropTypes.number.isRequired,
        type: PropTypes.string.isRequired,
        lastModifiedDate: PropTypes.instanceOf(Date).isRequired,
    }),
    onChange: PropTypes.func.isRequired,
    extraFileExtensions: PropTypes.arrayOf(PropTypes.string),
    dropText: PropTypes.string,
};
